// src/App.tsx

import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext.tsx';
import { AuthProvider } from './contexts/AuthContext.tsx';
import Login from './pages/Login.tsx';
import PasswordSetup from './pages/PasswordSetup.tsx';
import PasswordRecovery from './pages/PasswordRecovery.tsx';
import { MembersLanding, MembersAuth, MembersReferral } from './pages/members/MembersPublic.tsx';
import MembersPortal from './pages/members/MembersPortal.tsx';
import MembersOnboarding from './pages/members/MembersOnboarding.tsx';
import {MembersForgotPassword, MembersResetPassword} from './pages/members/MembersPasswordReset.tsx';
import MembersEmailHistory from './pages/members/MembersEmailHistory.tsx';
import MembersAdmin from './pages/members/MembersAdmin.tsx';
import { MembersDirectory, MembersDirectoryDetail } from './pages/members/MembersDirectory.tsx';
import MembersDirectoryAdmin from './pages/members/MembersDirectoryAdmin.tsx';
import Register from './pages/Register.tsx';
import TuringSetup from './pages/TuringSetup.tsx';
import NewClinicAdminPage from './pages/CreateNewClinicPage.tsx';
import PrivateRoute from './components/PrivateRoute.tsx';
import MyProfile, { FirstAccess } from './pages/MyProfile.tsx';
import ProtectedLayout from './components/ProtectedLayout.tsx';
import AppVersionGuard from './components/AppVersionGuard.tsx';
import PlatformAvailabilityNotice from './components/PlatformAvailabilityNotice.tsx';
import Dashboard from './pages/Dashboard.tsx';
import Metas from './pages/Metas.tsx';
// import CRMPage from './pages/CRM.tsx'; -> versão 1 do CRM
import ChatPage4 from './pages/Chat.tsx';

import FollowUp from './pages/FollowUp-v2.tsx';
import Workflows from './pages/Workflows.tsx';
import FollowUpModern from './pages/FollowUp-Modern.tsx';
import NoShow from './pages/NoShow.tsx';
import PosConsultaConfig from './pages/PosConsultaConfig.tsx';
import PosVendaConfig from './pages/PosVendaConfig.tsx';
import NutritionCampaignConfig from './pages/NutritionCampaignConfig.tsx';
import Disparo from './pages/Disparo.tsx';
import { FlowControlPanel } from './components/FlowControlPanel.tsx';
import PromptConfigPage from './pages/PromptConfig.tsx';
import CrmSettings from './pages/CrmSettings.tsx';
import ClinicConfigPage from './pages/ClinicConfig.tsx';
import WhatsAppConnectPage from './pages/WhatsAppConnect.tsx';

// <-- Importe as novas páginas -->
import AgendaIntegration from './pages/AgendaIntegration.tsx';
import FormLeadWebhookPage from './pages/FormLeadWebhook.tsx';
import SupportGroupIntegration from './pages/SupportGroupIntegration.tsx';
import AdminResources from './pages/AdminResources.tsx';
import { isCompanyRegistrationApprovalEnabled, isTuringSetupLinkEnabled } from './config/featureFlags.ts';
import AdminRegistrationApprovals from './pages/AdminRegistrationApprovals.tsx';


// Páginas em desenvolvimento
import AIResponseWindowsPage from './pages/AIResponseWindowsPage.tsx';
import AdCampaignDashboard from './pages/AdCampaignDashboard.tsx'
import DashboardMetrics from './pages/DashboardMetrics.tsx'
import CRMInterfacev4 from './pages/CRM_v3.tsx';
import CRMListPreview from './pages/CRMListPreview.tsx';
import { shouldEnableChatPresencePreview } from './pages/chatPresencePreviewGate.ts';
import Confirmation from './pages/ConfimacaoConsulta-v1.tsx'; // Versão 2.0 do Follow-up de confirmação de consulta
import ContactsList from './pages/ContactsList.tsx';
import ReferralCampaigns from './pages/ReferralCampaigns.tsx';
import ReportsPage from './pages/ReportsPage.tsx';
import TagsManagement from './pages/TagsManagement.tsx';
import WhatsAppTemplates from './pages/WhatsAppTemplates.tsx';
import CampanhasPage from './pages/Campanhas.tsx';
import McpAuthorize from './pages/McpAuthorize.tsx';
import McpConnections from './pages/McpConnections.tsx';
//import CRMInterfacev4 from './pages/test-dev.tsx';

const chatPresencePreviewEnabled = shouldEnableChatPresencePreview();
// Manter o teste literal no ponto do import permite que o bundler elimine o
// laboratório e seu chunk do grafo de produção.
const ChatPresencePreview = process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('./pages/ChatPresencePreview.tsx'))
  : null;

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <Router>
        <AppVersionGuard />
        <PlatformAvailabilityNotice />
        <Routes>
          <Route path="/definir-senha" element={<PasswordSetup />} />
          <Route path="/recuperar-senha" element={<PasswordRecovery />} />
          <Route path="/members" element={<MembersLanding />} />
          <Route path="/members/parceiros" element={<MembersDirectory />} />
          <Route path="/members/parceiros/:id" element={<MembersDirectoryDetail />} />
          <Route path="/members/register" element={<MembersOnboarding />} />
          <Route path="/members/forgot-password" element={<MembersForgotPassword />} />
          <Route path="/members/reset-password" element={<MembersResetPassword />} />
          <Route path="/members/login" element={<MembersAuth />} />
          <Route path="/members/indicar/:code" element={<MembersReferral />} />
          <Route path="/members/portal" element={<MembersPortal />} />
          {window.location.hostname === 'members.sminy.ai' && <Route path="/" element={<MembersLanding />} />}
          <Route path="*" element={<AuthProvider><Routes>
          <Route path="/" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {isTuringSetupLinkEnabled() && <Route path="/configurar/:token" element={<TuringSetup />} />}
          <Route path="/new-clinic-admin" element={<NewClinicAdminPage />} />
          <Route path="/mcp/authorize" element={<McpAuthorize />} />

          {/* Rotas privadas */}
          <Route element={<PrivateRoute />}>
            <Route path="/primeiro-acesso" element={<FirstAccess />} />
            {/* Layout protegido */}
            <Route element={<ProtectedLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/metas" element={<Metas />} />
              <Route path="/dashboard/ads" element={<AdCampaignDashboard />} />
              <Route path="/dashboard/metrics" element={<DashboardMetrics />} />
              <Route path="/crm" element={<CRMInterfacev4 />} />
              <Route path="/crm-list-preview" element={<CRMListPreview />} />
              {chatPresencePreviewEnabled && ChatPresencePreview && (
                <Route
                  path="/chat-presence-preview"
                  element={(
                    <React.Suspense fallback={null}>
                      <ChatPresencePreview />
                    </React.Suspense>
                  )}
                />
              )}
              <Route path="/campanhas" element={<CampanhasPage />} />
              <Route path="/contacts" element={<ContactsList />} />
              <Route path="/tags" element={<TagsManagement />} />
              {/*<Route path="/test-dev" element={<CRMInterfacev4 />} />*/}
              <Route path="/chat" element={<ChatPage4 />} />
              <Route path="/follow-up" element={<FollowUp />} />
              <Route path="/workflows" element={<Workflows />} />
              <Route path="/follow-up-modern" element={<FollowUpModern />} />
              <Route path="/follow-up/confirmacao" element={<Confirmation />} />
              <Route path="/follow-up/noshow" element={<NoShow />} />
              <Route path="/follow-up/pos-consulta" element={<PosConsultaConfig />} />
              <Route path="/follow-up/pos-venda" element={<PosVendaConfig />} />
              <Route path="/follow-up/nutricao" element={<NutritionCampaignConfig />} />
              <Route path="/follow-up/disparo" element={<Disparo />} />
              <Route path="/follow-up/controle-fluxos" element={<FlowControlPanel />} />
              <Route path="/configuracoes/crm" element={<CrmSettings />} />
              <Route path="/clinic" element={<ClinicConfigPage />} />
              <Route path="/meu-perfil" element={<MyProfile />} />
              <Route path="/clinic/controle-fluxos" element={<FlowControlPanel />} />
              <Route path="/canais" element={<WhatsAppConnectPage />} />
              <Route path="/whatsapp" element={<WhatsAppConnectPage />} />

              {/* Rotas de Prompt e Sub-Rotas */}
              <Route path="/prompt" element={<PromptConfigPage />} />
              <Route path="/prompt/clock-ai" element={<AIResponseWindowsPage />} />
              <Route path="/prompt/agenda" element={<AgendaIntegration />} />
              <Route path="/prompt/form-webhook" element={<FormLeadWebhookPage />} />
              <Route path="/prompt/support-group" element={<SupportGroupIntegration />} />
              <Route path="/prompt/indicacoes" element={<ReferralCampaigns />} />
              <Route path="/prompt/templates" element={<WhatsAppTemplates />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/members/admin/email-history" element={<MembersEmailHistory />} />
              <Route path="/members/admin/directory" element={<MembersDirectoryAdmin />} />
              <Route path="/members/admin" element={<MembersAdmin />} />
              <Route path="/admin/resources" element={<AdminResources />} />
              {isCompanyRegistrationApprovalEnabled() && <Route path="/admin/registrations" element={<AdminRegistrationApprovals />} />}
              <Route path="/configuracoes/conexoes-mcp" element={<McpConnections />} />

            </Route>
          </Route>
          </Routes></AuthProvider>} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;
